<!-- eslint-disable vue/valid-v-slot -->
<template>
    <div class="notifications-page">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Powiadomienia</h1>
        </div>
      </div>
      <div class="filter-section mb-3">
        <div class="search-input">
          <v-icon size="18" color="#6E7691">mdi-magnify</v-icon>
          <input v-model="search" type="text" :placeholder="$t('common.search')" />
        </div>
      </div>
  
      <v-data-table
        :headers="headers"
        :items="notifications"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        item-key="id"
        class="elevation-1"
        @click:row="selectNotification"
      >
        <template v-slot:item.read="{ item }">
          <v-chip :color="item.read ? 'green' : 'orange'" small>
            {{ item.read ? $t('notifications.read') : $t('notifications.unread') }}
          </v-chip>
        </template>
        <template v-slot:item.type="{ item }">
          <v-chip small :color="getTypeColor(item.type)" text-color="white">
            <v-icon small left>{{ getTypeIcon(item.type) }}</v-icon>
            {{ getTypeLabel(item.type) }}
          </v-chip>
        </template>
        <template v-slot:item.createdAt="{ item }">
          {{ new Date(item.createdAt).toLocaleString() }}
        </template>
      </v-data-table>
  
      <!-- Dialog szczegółów powiadomienia -->
      <v-dialog v-model="dialog" max-width="600">
        <v-card>
          <v-card-title>
            <span class="headline">{{ $t('notifications.detailsTitle') }}</span>
          </v-card-title>
          <v-card-text>
            <p><strong>{{ $t('common.status') }}:</strong>
              <v-chip :color="selectedNotification.read ? 'green' : 'orange'" small>
                {{ selectedNotification.read ? $t('notifications.read') : $t('notifications.unread') }}
              </v-chip>
            </p>
            <p><strong>{{ $t('notifications.text') }}:</strong> {{ selectedNotification.text }}</p>
            <p><strong>{{ $t('notifications.createdAt') }}:</strong> {{ new Date(selectedNotification.createdAt).toLocaleString() }}</p>

            <!-- Szczegóły JSON -->
            <div v-if="selectedNotification.json" class="mt-4">
              <h4>{{ $t('notifications.details') }}:</h4>
              <div class="notification-details">
                <p v-if="selectedNotification.json.type"><strong>{{ $t('notifications.type') }}:</strong> {{ selectedNotification.json.type }}</p>
                <p v-if="selectedNotification.json.reason"><strong>{{ $t('notifications.reason') }}:</strong> {{ selectedNotification.json.reason }}</p>
                <p v-if="selectedNotification.json.approvedBy"><strong>{{ $t('notifications.approvedById') }}:</strong> {{ selectedNotification.json.approvedBy }}</p>
                <p v-if="selectedNotification.json.approvedAt"><strong>{{ $t('notifications.approvedAt') }}:</strong> {{ new Date(selectedNotification.json.approvedAt).toLocaleString() }}</p>
                <p v-if="selectedNotification.json.rejectedBy"><strong>{{ $t('notifications.rejectedById') }}:</strong> {{ selectedNotification.json.rejectedBy }}</p>
                <p v-if="selectedNotification.json.rejectedAt"><strong>{{ $t('notifications.rejectedAt') }}:</strong> {{ new Date(selectedNotification.json.rejectedAt).toLocaleString() }}</p>
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              v-if="selectedNotification.json && selectedNotification.json.taskId"
              color="primary" depressed
              @click="goToTask(selectedNotification.json.taskId)"
            >
              <v-icon left small>mdi-clipboard-arrow-right-outline</v-icon>Przejdź do zadania
            </v-btn>
            <v-btn
              v-if="selectedNotification.json && selectedNotification.json.type === 'ADMIN_BOLT_SUSPENSION' && selectedNotification.json.driverId"
              color="primary" depressed
              @click="goToDriver(selectedNotification.json.driverId)"
            >
              <v-icon left small>mdi-account-arrow-right-outline</v-icon>Przejdź do kierowcy
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="dialog = false">{{ $t('common.close') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </template>
  
  <script>
  import axios from "axios";
  import Swal from "sweetalert2";
  
  export default {
    data() {
      return {
        search: "",
        options: {
          page: 1,
          itemsPerPage: 10,
          sortBy: [],
          sortDesc: [],
        },
        headers: [
          { text: "Status", value: "read", width: "120px" },
          { text: "Typ", value: "type", width: "200px" },
          { text: "Data", value: "createdAt", width: "180px" },
          { text: "Treść", value: "text" },
        ],
        notifications: [],
        totalItems: 0,
        loading: false,
        dialog: false,
        selectedNotification: {},
      };
    },
    watch: {
      options: {
        handler() {
          this.initialize();
        },
        deep: true,
      },
      search() {
        this.options.page = 1;
        this.initialize();
      },
    },
    methods: {
      async initialize() {
        this.loading = true;
        try {
          const { itemsPerPage, page } = this.options;
          const response = await axios({
            method: "get",
            url:
              process.env.VUE_APP_ROOT_API +
              `notifications/user/all?take=${itemsPerPage}&skip=${((page - 1) * itemsPerPage) + 1}&search=${this.search}`,
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          });
  
          this.notifications = response.data.data.result.map((n) => ({
            id: n.id,
            text: n.text,
            type: n.json?.type || 'general',
            createdAt: n.createdAt,
            read: n.read,
            json: n.json,
          }));
  
          this.totalItems = response.data.data.count;
        } catch (e) {
          console.error(e);
          Swal.fire("Błąd", e.response?.data?.message || "Nieznany błąd", "error");
        } finally {
          this.loading = false;
        }
      },
    getTypeLabel(type) {
      const labels = {
        'ADMIN_NEW_DRIVER_REGISTRATION': 'Rejestracja',
        'ADMIN_DOCUMENTS_SUBMITTED': 'Dokumenty',
        'ADMIN_NEW_TASK': 'Zgłoszenie',
        'ADMIN_TASK_NOTE_FROM_DRIVER': 'Zgłoszenie',
        'ADMIN_DRIVER_CHANGES_REQUEST': 'Dane kierowcy',
        'ADMIN_IMPORT_ERROR': 'Import',
        'ADMIN_VEHICLE_INSURANCE_EXPIRING': 'Flota',
        'ADMIN_VEHICLE_INSPECTION_EXPIRING': 'Flota',
        'ADMIN_VEHICLE_SERVICE_DUE': 'Flota',
        'ADMIN_DRIVER_SERVICE_SUBMITTED': 'Serwis do akceptacji',
        'ADMIN_CRASH_REPORTED': 'Szkoda',
        'TASK_ASSIGNED': 'Zgłoszenie',
        'DOCUMENT_EXPIRING_30': 'Dokument',
        'DOCUMENT_EXPIRING_7': 'Dokument',
        'DOCUMENT_EXPIRED': 'Dokument',
      };
      return labels[type] || 'Ogólne';
    },
    getTypeColor(type) {
      const colors = {
        'ADMIN_NEW_DRIVER_REGISTRATION': 'blue',
        'ADMIN_DOCUMENTS_SUBMITTED': 'teal',
        'ADMIN_NEW_TASK': 'orange',
        'ADMIN_TASK_NOTE_FROM_DRIVER': 'blue',
        'ADMIN_DRIVER_CHANGES_REQUEST': 'purple',
        'ADMIN_IMPORT_ERROR': 'red',
        'ADMIN_VEHICLE_INSURANCE_EXPIRING': 'orange',
        'ADMIN_VEHICLE_INSPECTION_EXPIRING': 'orange',
        'ADMIN_VEHICLE_SERVICE_DUE': 'amber',
        'ADMIN_DRIVER_SERVICE_SUBMITTED': 'orange',
        'ADMIN_CRASH_REPORTED': 'red',
        'TASK_ASSIGNED': 'green',
        'DOCUMENT_EXPIRING_7': 'orange',
        'DOCUMENT_EXPIRED': 'red',
      };
      return colors[type] || 'blue';
    },
    getTypeIcon(type) {
      const icons = {
        'ADMIN_NEW_DRIVER_REGISTRATION': 'mdi-account-plus',
        'ADMIN_DOCUMENTS_SUBMITTED': 'mdi-file-document-check',
        'ADMIN_NEW_TASK': 'mdi-clipboard-alert',
        'ADMIN_TASK_NOTE_FROM_DRIVER': 'mdi-comment-text',
        'ADMIN_DRIVER_CHANGES_REQUEST': 'mdi-account-edit',
        'ADMIN_IMPORT_ERROR': 'mdi-alert-circle',
        'ADMIN_VEHICLE_INSURANCE_EXPIRING': 'mdi-shield-alert',
        'ADMIN_VEHICLE_INSPECTION_EXPIRING': 'mdi-car-wrench',
        'ADMIN_VEHICLE_SERVICE_DUE': 'mdi-car-cog',
        'ADMIN_DRIVER_SERVICE_SUBMITTED': 'mdi-car-wrench',
        'ADMIN_CRASH_REPORTED': 'mdi-car-emergency',
        'TASK_ASSIGNED': 'mdi-clipboard-account',
        'DOCUMENT_EXPIRING_30': 'mdi-file-clock',
        'DOCUMENT_EXPIRING_7': 'mdi-file-alert',
        'DOCUMENT_EXPIRED': 'mdi-file-cancel',
        'ADMIN_BOLT_SUSPENSION': 'mdi-account-cancel',
      };
      return icons[type] || 'mdi-bell';
    },
      goToDriver(driverId) {
        this.dialog = false;
        location.href = '/driver-details?id=' + driverId;
      },
      goToTask(taskId) {
        this.dialog = false;
        this.$router.push({ name: 'TaskPageVue', query: { taskId } }).catch(() => {});
      },
      async selectNotification(notification) {
        this.selectedNotification = notification;
        this.dialog = true;

        // Oznacz jako przeczytane jeśli jeszcze nie przeczytane
        if (!notification.read) {
          try {
            await axios({
              method: "patch",
              url: process.env.VUE_APP_ROOT_API + `notifications/user/read/${notification.id}`,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            // Update local state
            notification.read = true;
            if (notification.type === 'ADMIN_TASK_NOTE_FROM_DRIVER') {
              this.$root.$emit('task-notifications-updated');
            }
          } catch (e) {
            console.error('Nie udało się oznaczyć powiadomienia jako przeczytane:', e);
          }
        }
      },
    },
    mounted() {
      this.initialize();
    },
  };
  </script>

<style scoped>
.notifications-page {
  background: #181C28;
  min-height: calc(100vh - 56px);
  padding: 16px 16px 40px;
  margin: -24px;
  color: #fff;
}
.page-header { padding: 4px 4px 18px; }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; text-transform: capitalize; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }

.filter-section {
  background: #232838;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 18px;
  padding: 14px 16px;
}
.search-input {
  display: flex; align-items: center; gap: 8px;
  background: #181C28;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 10px 14px;
}
.search-input input {
  flex: 1; background: transparent; border: none; outline: none;
  color: #fff; font-size: 14px; font-family: inherit;
}
.search-input input::placeholder { color: #6E7691; }

.notifications-page >>> .v-card {
  background: #232838 !important;
  border: 1px solid rgba(255,255,255,0.06) !important;
  border-radius: 18px !important;
  box-shadow: none !important;
  color: #fff !important;
}
.notifications-page >>> .v-card .v-card__title { color: #fff !important; padding: 16px 20px 8px !important; }
.notifications-page >>> .v-data-table {
  background: #232838 !important;
  border: 1px solid rgba(255,255,255,0.06) !important;
  border-radius: 18px !important;
  color: #fff !important;
  box-shadow: none !important;
}
.notifications-page >>> .v-data-table thead tr th {
  background: #1F2434 !important;
  color: #6E7691 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid rgba(255,255,255,0.06) !important;
}
.notifications-page >>> .v-data-table tbody tr { background: #232838 !important; cursor: pointer; }
.notifications-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.notifications-page >>> .v-data-table tbody tr td {
  color: #fff !important;
  border-bottom: 1px solid rgba(255,255,255,0.04) !important;
}
.notifications-page >>> .v-data-footer {
  background: #1F2434 !important;
  color: #B0BEC5 !important;
  border-top: 1px solid rgba(255,255,255,0.06) !important;
}
.notifications-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.notifications-page >>> .v-chip.green { background: rgba(45,203,115,0.18) !important; color: #6FE89A !important; }
.notifications-page >>> .v-chip.orange { background: rgba(255,152,0,0.18) !important; color: #FFB74D !important; }
.notifications-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.notifications-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.notifications-page >>> .v-dialog .v-card { background: #232838 !important; }
.notifications-page >>> .notification-details { color: #B0BEC5; }
.notifications-page >>> strong { color: #fff; }
</style>
